import { useEffect, useState, type RefObject } from 'react';

/**
 * Marca `data-revealed="true"` em cada `[data-reveal]` dentro de `root` quando
 * ele entra na tela, uma vez só. O CSS decide o que "aparecer" significa — e,
 * com `prefers-reduced-motion`, não anima nada.
 *
 * Um observador para a página inteira em vez de um por componente: são
 * dezenas de alvos, e cada `IntersectionObserver` a mais é trabalho repetido
 * a cada rolagem.
 */
export function useReveal(root: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const container = root.current;
    if (!container) return;

    const targets = container.querySelectorAll<HTMLElement>('[data-reveal]');

    if (typeof IntersectionObserver === 'undefined') {
      targets.forEach((target) => (target.dataset.revealed = 'true'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          (entry.target as HTMLElement).dataset.revealed = 'true';
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.1 },
    );

    targets.forEach((target) => observer.observe(target));
    return () => observer.disconnect();
  }, [root]);
}

/** `true` enquanto o elemento estiver (perto de estar) na tela. */
export function useInView(ref: RefObject<HTMLElement | null>, rootMargin = '120px') {
  // Sem IntersectionObserver (jsdom, navegador antigo) o elemento conta como visível.
  const [inView, setInView] = useState(() => typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry?.isIntersecting ?? false),
      { rootMargin },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, rootMargin]);

  return inView;
}

export function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
